import React from "react";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from "recharts";

const card = "rounded-xl border border-[#00ff90]/30 bg-[#0d0d0d] shadow-[0_0_12px_#00ff9022]";
const tooltipStyle = {
  contentStyle: { background: "#0d0d0d", border: "1px solid #00ff9033", borderRadius: 8, fontSize: 11 },
  labelStyle: { color: "#00ff90" },
};

const STATUS_COLORS = {
  inOffice: "#00ff90",
  remote:   "#60a5fa",
  onLeave:  "#fbbf24",
  out:      "#f87171",
};

const EXCEPTION_STYLE = {
  LATE:          { color: "#fbbf24", label: "Late"           },
  MISSING_PUNCH: { color: "#f87171", label: "Missing Punch"  },
  SHORT_HOURS:   { color: "#fb923c", label: "Short Hours"    },
  NO_SHOW:       { color: "#f43f5e", label: "No Show"        },
  OVERTIME:      { color: "#60a5fa", label: "Overtime"       },
};

export default function DailyOpsBoard({ loading, data }) {
  if (loading) return (
    <div className={`${card} p-5`}>
      <div className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium mb-4">Daily Ops — Today</div>
      <div className="h-48 rounded-lg bg-white/5 animate-pulse" />
    </div>
  );

  if (!data) return (
    <div className={`${card} p-5`}>
      <div className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium mb-4">Daily Ops — Today</div>
      <p className="text-white/20 text-sm italic text-center py-4">No data available.</p>
    </div>
  );

  const donutData = [
    { name: "In Office", value: data.inOffice || 0, color: STATUS_COLORS.inOffice },
    { name: "Remote",    value: data.remote   || 0, color: STATUS_COLORS.remote   },
    { name: "On Leave",  value: data.onLeave  || 0, color: STATUS_COLORS.onLeave  },
    { name: "Out",       value: data.out      || 0, color: STATUS_COLORS.out      },
  ].filter(d => d.value > 0);

  const total      = donutData.reduce((s, d) => s + d.value, 0);
  const exceptions = data.exceptions || [];
  const shifts     = data.shifts     || [];

  return (
    <div className={card}>
      <div className="px-5 py-4 border-b border-white/5 flex items-center justify-between">
        <span className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium">Daily Ops — Today</span>
        <span className="text-xs text-white/30">{total} total</span>
      </div>
      <div className="p-5 space-y-5">

        {/* Headcount donut */}
        {donutData.length > 0 && (
          <div className="flex items-center gap-4">
            <ResponsiveContainer width={110} height={110}>
              <PieChart>
                <Pie data={donutData} cx="50%" cy="50%" innerRadius={28} outerRadius={48} paddingAngle={2} dataKey="value">
                  {donutData.map((d, i) => <Cell key={i} fill={d.color} fillOpacity={0.85} />)}
                </Pie>
                <Tooltip {...tooltipStyle} formatter={(v, n) => [`${v} people`, n]} />
              </PieChart>
            </ResponsiveContainer>
            <div className="flex-1 grid grid-cols-2 gap-2">
              {donutData.map(d => (
                <div key={d.name} className="rounded-lg border border-white/5 bg-white/3 p-2.5">
                  <p className="text-xs text-white/30">{d.name}</p>
                  <p className="text-lg font-bold" style={{ color: d.color }}>{d.value}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Exceptions */}
        {exceptions.length > 0 && (
          <div>
            <p className="text-xs text-white/30 uppercase tracking-wider mb-2">Exceptions Today</p>
            <div className="flex flex-wrap gap-2">
              {exceptions.map((e, i) => {
                const cfg = EXCEPTION_STYLE[e.type] || { color: "#ffffff44", label: e.type };
                return (
                  <span key={i} className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-white/5 bg-white/3">
                    <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: cfg.color }} />
                    <span className="text-white/50">{cfg.label}</span>
                    <span className="font-bold" style={{ color: cfg.color }}>{e.count}</span>
                  </span>
                );
              })}
            </div>
          </div>
        )}

        {/* Shift coverage */}
        {shifts.length > 0 && (
          <div>
            <p className="text-xs text-white/30 uppercase tracking-wider mb-2">Shift Coverage</p>
            <div className="space-y-2.5">
              {shifts.map((s, i) => {
                const pct = Math.min(100, Math.round((s.coverage / Math.max(1, s.required)) * 100));
                const color = pct >= 90 ? "#00ff90" : pct >= 70 ? "#fbbf24" : "#f87171";
                return (
                  <div key={i}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs text-white/50">{s.name}</span>
                      <span className="text-xs font-medium" style={{ color }}>{s.coverage}/{s.required}</span>
                    </div>
                    <div className="h-1.5 rounded-full bg-white/5">
                      <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: color }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}